import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { type TableFeatures } from '@/components/composed/DataTable/table-features';
import { Button } from '@/components/ui/button';
import type { Permission, PermissionSet } from '@/lib/api/domains/permissions';
import {
  permissionCatalogQuery,
  permissionSetsQuery,
} from '../api/permission-queries';

/*
 * The permission-set LIST (F03 R51, R52). Built for tasks 4.2 and 5.2 against
 * the task-4.1 tests in PermissionSetList.test.tsx and the task-5.1 tests in
 * PermissionSetListContents.test.tsx:
 *
 *   - AC-77cffe6c (R51) — THE permission set list SHALL show a set containing
 *     no permissions as ADDING NOTHING. An empty set (empty `permissionIds` — a
 *     valid, accepted state, AC-14c5e3e6) carries, on its row, a plain "adds
 *     nothing" label so a half-composed set is not mistaken for a deliberate
 *     one; a set that grants something never carries it.
 *   - AC-ad1c6f62 (F03 rollup, LIST half) — THE system SHALL state plainly,
 *     WHEREVER a permission set grants nothing, that it grants nothing. The list
 *     is one of the two places the wording appears (the other is the composer,
 *     on save — PermissionSetComposer).
 *   - AC-971e8826 (R52) — THE permission set list SHALL show the permissions
 *     each set contains WITHOUT the administrator opening the set. Each set's
 *     row names the permissions it contains, read from the list itself — no
 *     composer opened.
 *   - AC-e4f0268c (R52) — WHERE the list shows fewer permissions than a set
 *     contains, THE list SHALL say how many MORE there are. A set holding more
 *     than the preview shows the first few and a "+N more" affordance where N is
 *     exactly the remaining count.
 *
 * Tables are the primary surface (product steering): the list is a DataTable
 * (compact density, the composed component the catalog uses) with one row per
 * set. The sets are relayed from Identity unchanged (domain-svc internal/rbac);
 * the list reads `permissionSetsQuery` and renders exactly those sets, composing
 * none of its own. The contents preview resolves each set's `permissionIds`
 * against the SAME catalog the composer reads (`permissionCatalogQuery`), so a
 * permission reads as its ACTION — no second transport, no shape change, the
 * catalog is already available (PermissionSetComposer does the same resolution).
 *
 * F03 records; F03b refuses — the "adds nothing" wording and the contents
 * preview are DISPLAY over Identity data, not a refusal (an empty set is a
 * valid, accepted state, AC-14c5e3e6). Nothing here is an authorization
 * decision.
 */

/** A set grants nothing when it contains no permissions (R51). */
function grantsNothing(set: PermissionSet): boolean {
  return set.permissionIds.length === 0;
}

/*
 * The row-level contents cap (AC-e4f0268c): a row names at most the first three
 * permissions a set contains and, where a set holds more, says "+N more" with N
 * the exact remaining count. Three is a restrained, scannable preview for a
 * compact table (product steering: tables are the primary surface).
 */
const PREVIEW_LIMIT = 3;

/** The actions of a set's permissions, resolved against the catalog (R52). */
function resolveActions(
  set: PermissionSet,
  catalogById: Map<string, Permission>,
): string[] {
  return set.permissionIds
    .map((id) => catalogById.get(id)?.action)
    .filter((action): action is string => action !== undefined);
}

export function PermissionSetList({
  onCompose,
  onHistory,
}: {
  /*
   * Open the composer FOR an existing set (R49, AC-cf93b725). When supplied,
   * each row carries a way-in that hands the set back to the caller (the route),
   * which opens the composer with that set's id so its current permissions show
   * as already chosen. The list itself opens no composer — it only names the set
   * to compose; the owning route holds the composer state (the route stays thin,
   * the list stays a read surface). Omitted where the list is shown read-only
   * (the existing list tests render it with no way-in).
   */
  onCompose?: ((set: PermissionSet) => void) | undefined;
  /*
   * Open the as-of-date HISTORY surface for a set (R48/F69, AC-b8ffb235). When
   * supplied, each row carries a "History" way-in that hands the set back; the
   * route opens the read-only point-in-time history for that set. The list
   * opens nothing itself. Omitted where the list is read-only.
   */
  onHistory?: ((set: PermissionSet) => void) | undefined;
} = {}): React.ReactElement {
  const { data: setData, isLoading, isError } = useQuery(permissionSetsQuery());
  // The catalog that resolves each set's permission ids to their display text
  // (R52), the same read the composer uses. The preview shows a permission's
  // action, so the list and the composer agree on how a permission reads.
  const { data: catalogData } = useQuery(permissionCatalogQuery());

  const rows = setData ?? [];

  // Index the catalog by id so each row resolves its permissions in one pass.
  // Memoised because the lookup is rebuilt only when the catalog changes, not on
  // every render as the table re-renders rows — the React Compiler cannot see
  // that the Map identity must be stable across unrelated renders.
  const catalogById = useMemo(() => {
    const index = new Map<string, Permission>();
    for (const permission of catalogData ?? []) {
      index.set(permission.id, permission);
    }
    return index;
  }, [catalogData]);

  const columns: Array<ColumnDef<TableFeatures, PermissionSet>> = [
    {
      // The set's name, with — WHERE the set grants nothing — the plain "adds
      // nothing" label beside it on the one row (AC-77cffe6c, AC-ad1c6f62). A
      // set that grants something shows its name alone.
      id: 'name',
      header: 'Permission set',
      accessorKey: 'name',
      cell: ({ row }) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">{row.original.name}</span>
          {grantsNothing(row.original) ? (
            <span className="text-muted-foreground text-sm">
              Adds nothing to what its holders&rsquo; roles already allow
            </span>
          ) : null}
        </span>
      ),
    },
    {
      // What the set contains, read from the list itself without opening it
      // (AC-971e8826): the first few permissions' actions, resolved against the
      // catalog, and — WHERE the set holds more than the preview — a "+N more"
      // affordance with the exact remaining count (AC-e4f0268c). An empty set
      // grants nothing and shows no contents; its "adds nothing" label is on the
      // name cell.
      id: 'contents',
      header: 'Contains',
      cell: ({ row }) => {
        const actions = resolveActions(row.original, catalogById);
        if (actions.length === 0) return null;
        const shown = actions.slice(0, PREVIEW_LIMIT);
        const remaining = actions.length - shown.length;
        return (
          <span className="flex flex-wrap items-center gap-2">
            {shown.map((action, index) => (
              <span
                // The action text is not unique across sets, so the index pins
                // the position within this row's own preview.
                key={`${row.original.id}-${index}`}
                className="text-sm"
              >
                {action}
              </span>
            ))}
            {remaining > 0 ? (
              <span className="text-muted-foreground text-sm">
                +{remaining} more
              </span>
            ) : null}
          </span>
        );
      },
    },
  ];

  return (
    <section aria-label="Permission sets">
      <DataTable<PermissionSet>
        columns={columns}
        data={rows}
        getRowId={(row) => row.id}
        tableId="permission-sets"
        isLoading={isLoading}
        isError={isError}
        errorMessage="Failed to load the permission sets."
        emptyTitle="No permission sets"
        emptyDescription="No permission sets have been defined yet."
        /*
         * The per-row way-in to the composer FOR this set (R49, AC-cf93b725),
         * rendered only when the owning route supplied `onCompose`. It hands the
         * set back; the route opens the composer with its id so the set's current
         * permissions show as already chosen. The list opens no composer of its
         * own. Shown only to someone the server would let compose — the way-in is
         * the route's to gate (courtesy half); the server is the authority. F03
         * records; F03b refuses — this names a set to edit, it decides nothing.
         */
        actions={
          onCompose || onHistory
            ? (set) => (
                <span className="flex items-center justify-end gap-1">
                  {onCompose ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onCompose(set)}
                    >
                      Edit
                    </Button>
                  ) : null}
                  {onHistory ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onHistory(set)}
                    >
                      History
                    </Button>
                  ) : null}
                </span>
              )
            : undefined
        }
        actionsLabel="Actions"
      />
    </section>
  );
}
